import { useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { BeforeAfterSlider } from './BeforeAfterSlider'

interface ImageItem {
  type: 'image'
  src: string
  alt: string
}

interface PairItem {
  type: 'pair'
  before: string
  after: string
  title: string
}

export type ShowcaseItem = ImageItem | PairItem

interface ProjectCategoryShowcaseProps {
  index: number
  title: string
  description: string
  items: ShowcaseItem[]
  beforeLabel?: string
  afterLabel?: string
  previousLabel?: string
  nextLabel?: string
  goToLabel?: string
}

export function ProjectCategoryShowcase({
  index,
  title,
  description,
  items,
  beforeLabel = 'Prima',
  afterLabel = 'Dopo',
  previousLabel = 'Precedente',
  nextLabel = 'Successiva',
  goToLabel = 'Vai alla slide',
}: ProjectCategoryShowcaseProps) {
  const [current, setCurrent] = useState(0)
  const count = items.length

  if (count === 0) return null

  const next = () => setCurrent((c) => (c + 1) % count)
  const prev = () => setCurrent((c) => (c - 1 + count) % count)
  const goTo = (i: number) => setCurrent(i)

  const isReversed = index % 2 === 1
  const sectionNumber = String(index + 1).padStart(2, '0')
  const bgClass = index % 2 === 0 ? 'bg-surface' : 'bg-bg'

  return (
    <section className={`py-16 md:py-24 overflow-hidden ${bgClass}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Testo */}
            <div
              className={[
                'lg:col-span-5',
                isReversed ? 'lg:col-start-8' : 'lg:col-start-1',
                isReversed ? 'lg:order-2' : 'lg:order-1',
              ].join(' ')}
            >
              <div className="flex items-start gap-4 md:gap-6">
                <span
                  className="hidden md:block font-display text-7xl font-bold text-border leading-none select-none"
                  aria-hidden="true"
                >
                  {sectionNumber}
                </span>
                <div>
                  <h2 className="font-display text-h2 text-heading">{title}</h2>
                </div>
              </div>

              <p className="mt-6 text-base md:text-lg text-text leading-relaxed max-w-xl">
                {description}
              </p>

              {/* Controlli carosello */}
              {count > 1 && (
                <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={prev}
                      className="p-2.5 rounded-full border border-border bg-surface text-heading hover:border-brand hover:text-brand transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                      aria-label={previousLabel}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <span className="min-w-[3.5rem] text-center text-sm font-semibold text-text-muted">
                      {String(current + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
                    </span>
                    <button
                      type="button"
                      onClick={next}
                      className="p-2.5 rounded-full border border-border bg-surface text-heading hover:border-brand hover:text-brand transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                      aria-label={nextLabel}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="flex items-center gap-2" role="tablist" aria-label="Indicatori slide">
                    {items.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => goTo(i)}
                        role="tab"
                        aria-selected={i === current}
                        aria-label={`${goToLabel} ${i + 1}`}
                        className={[
                          'h-1.5 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
                          i === current ? 'w-8 bg-brand' : 'w-3 bg-border hover:bg-text-muted',
                        ].join(' ')}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Carosello */}
            <div
              className={[
                'lg:col-span-7',
                isReversed ? 'lg:col-start-1' : 'lg:col-start-6',
                isReversed ? 'lg:order-1' : 'lg:order-2',
              ].join(' ')}
            >
              <div className="relative rounded-2xl bg-surface p-2 md:p-3 shadow-xl shadow-black/5">
                <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-heading">
                  {items.map((item, i) => (
                    <div
                      key={i}
                      className={[
                        'absolute inset-0 transition-opacity duration-500',
                        i === current ? 'opacity-100 z-10' : 'opacity-0 z-0',
                      ].join(' ')}
                      aria-hidden={i !== current}
                    >
                      {item.type === 'pair' ? (
                        <BeforeAfterSlider
                          before={item.before}
                          after={item.after}
                          title={item.title}
                          beforeLabel={beforeLabel}
                          afterLabel={afterLabel}
                          hideCaption
                        />
                      ) : (
                        <img
                          src={item.src}
                          alt={item.alt}
                          className="h-full w-full object-cover"
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Bordo decorativo in evidenza */}
                <div
                  className="absolute -inset-px rounded-2xl border border-brand/10 pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </div>
    </section>
  )
}
